import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Instagram, Gift, Ticket } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { PRODUCTS } from '@/data/products';
import { IMAGES, SOCIAL_FEED, BRAND, DIVERSITY_GALLERY } from '@/data/brand';
import ProductCard from '@/components/ProductCard';
import { HOME, REGISTRATION as REG_IDS } from '@/constants/testIds';

const MarqueeRow = ({ items }) => (
  <div className="relative overflow-hidden bg-[#F4DD8C] text-[#FF2D9C] py-5">
    <div className="flex gap-12 whitespace-nowrap animate-marquee">
      {[...items, ...items].map((it, i) => (
        <span key={i} className="inline-flex items-center gap-3 font-display text-xl md:text-3xl font-bold tracking-tighter">
          {it} <Sparkles className="w-5 h-5 text-[#FF2D9C]" />
        </span>
      ))}
    </div>
  </div>
);

export default function Home() {
  const { t, lang } = useLang();

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] md:min-h-[92vh] overflow-hidden bg-[#EFEAFB]">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Jelly by Enkor model"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#EFEAFB] via-[#EFEAFB]/10 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#EFEAFB]/50 via-transparent to-transparent" />
        </div>

        {/* Floating blobs - lavender + yellow */}
        <div className="hidden md:block absolute top-1/4 right-[8%] w-44 h-44 bg-[#A99BE0] rounded-full opacity-80 animate-blob blur-[2px] mix-blend-multiply" />
        <div className="hidden md:block absolute bottom-[18%] left-[6%] w-36 h-36 bg-[#F4DD8C] rounded-full opacity-80 animate-blob blur-[1px] mix-blend-multiply" />
        <div className="hidden md:block absolute top-[18%] left-[14%] w-20 h-20 bg-[#FF7597] rounded-full opacity-90 shadow-[0_8px_30px_rgba(169,155,224,0.35)] animate-float-medium" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-28 pb-20 flex flex-col h-full">
          <span className="self-start text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#1F1A24] bg-[#F4DD8C] px-4 py-2 rounded-full shadow-[0_8px_24px_rgba(244,221,140,0.4)]">
            {t('hero.eyebrow')}
          </span>

          <h1 className="font-display mt-8 md:mt-12 text-5xl sm:text-7xl lg:text-8xl xl:text-9xl font-black tracking-tighter text-[#1F1A24] max-w-5xl text-balance leading-[0.9]">
            {t('hero.title')}
          </h1>

          <p className="mt-6 max-w-xl text-base md:text-lg text-[#1F1A24]/80 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/productos"
              data-testid={HOME.heroCta}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#FF7597] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] hover:scale-105 transition-all shadow-[0_12px_40px_rgba(255,117,151,0.45)]"
            >
              {t('hero.cta')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/sobre"
              data-testid={HOME.heroExplore}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#1F1A24] text-sm font-bold tracking-widest uppercase border border-[#1F1A24]/10 hover:border-[#FF7597] transition-colors"
            >
              {t('hero.explore')}
            </Link>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <MarqueeRow items={[
        'Jelly Drop 01',
        'Cruelty Free',
        'TikTok Approved',
        'Vegan Friendly',
        'Made for Gen Z',
        'Powered by Enkor',
      ]} />

      {/* COLLECTION */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-[#FFFBFB]" id="collection" data-testid={HOME.collection}>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div className="relative">
            <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/5] bg-[#F6EDFA] grain">
              <img src={IMAGES.jellyBg} alt="Jelly textures" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-8 -right-8 w-44 rounded-3xl overflow-hidden border-4 border-white shadow-2xl rotate-6">
              <img src={IMAGES.textureSmear} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -top-6 -left-6 bg-[#FF7597] text-white p-5 rounded-3xl shadow-xl rotate-[-6deg]">
              <p className="text-[10px] font-bold tracking-[0.3em] uppercase">New</p>
              <p className="font-display text-3xl font-black tracking-tighter">Drop 01</p>
            </div>
          </div>

          <div>
            <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#FF7597]">
              {t('home.collectionEyebrow')}
            </p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#1F1A24] text-balance">
              {t('home.collectionTitle')}
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#574A66] leading-relaxed max-w-md">
              {t('home.collectionBody')}
            </p>

            <div className="mt-10 grid grid-cols-2 gap-4 md:gap-6 max-w-md">
              {[
                { v: '+20', l: t('home.statsExperience') },
                { v: '12', l: t('home.statsCountries') },
                { v: '50k', l: t('home.statsHappy') },
                { v: '25+', l: t('home.statsShades') },
              ].map((s, i) => (
                <div key={i} className="rounded-3xl bg-white border border-[#FF7597]/10 p-5 shadow-[0_8px_32px_rgba(255,117,151,0.08)]">
                  <p className="font-display text-3xl md:text-4xl font-black tracking-tighter text-[#FF7597]">{s.v}</p>
                  <p className="text-xs mt-1 text-[#574A66]">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-[#FFF2F5]" data-testid={HOME.featured}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#FF7597]">
                {t('home.featuredEyebrow')}
              </p>
              <h2 className="font-display mt-3 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#1F1A24] max-w-2xl text-balance">
                {t('home.featuredTitle')}
              </h2>
            </div>
            <Link
              to="/productos"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F1A24] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#FF7597] transition-colors"
            >
              {t('home.featuredCta')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {PRODUCTS.slice(0, 4).map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* DIVERSITY — Para toda piel */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-[#FFFBFB]" data-testid="home-diversity">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#FF7597]">
                {t('home.diversityEyebrow')}
              </p>
              <h2 className="font-display mt-3 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#1F1A24] max-w-3xl text-balance">
                {t('home.diversityTitle')}
              </h2>
              <p className="mt-4 text-base md:text-lg text-[#574A66] max-w-xl">
                {t('home.diversityBody')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {['#01', '#02', '#03', '#04', '#05', '#06'].map((s, i) => {
                const swatches = ['#F5DBC2', '#E7BFA0', '#D29A78', '#B57852', '#8A5535', '#5C3621'];
                return (
                  <span
                    key={s}
                    className="h-9 w-9 rounded-full border-2 border-white shadow-md"
                    style={{ backgroundColor: swatches[i] }}
                    aria-label={`Tono ${s}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Bento gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {DIVERSITY_GALLERY.map((it, i) => {
              const spans = [
                'row-span-2 aspect-[3/5]',
                'aspect-square',
                'aspect-square',
                'row-span-2 aspect-[3/5]',
                'aspect-square',
                'row-span-2 aspect-[3/5]',
                'aspect-square',
                'aspect-square',
              ];
              return (
                <div
                  key={i}
                  className={`relative group rounded-3xl overflow-hidden bg-[#F6EDFA] ${spans[i] || 'aspect-square'}`}
                >
                  <img
                    src={it.src}
                    alt={it.tag[lang]}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                    <span className="text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-white">
                      {it.tag[lang]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOCIAL FEED */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-[#FFFBFB]" data-testid={HOME.socialFeed}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#FF7597]">
                {t('home.socialEyebrow')}
              </p>
              <h2 className="font-display mt-3 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#1F1A24] max-w-2xl text-balance">
                {t('home.socialTitle')}
              </h2>
              <p className="mt-4 text-[#574A66] max-w-md">{t('home.socialBody')}</p>
            </div>
            <div className="flex gap-2">
              <a
                href={`https://instagram.com/${BRAND.instagram}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1F1A24] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#FF7597] transition-colors"
              >
                <Instagram className="w-4 h-4" /> @{BRAND.instagram}
              </a>
            </div>
          </div>

          {/* Bento feed */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
            {SOCIAL_FEED.map((it, i) => {
              const span = [
                'row-span-2',
                '',
                'row-span-2',
                '',
                '',
                'row-span-2',
              ][i] || '';
              return (
                <a
                  key={i}
                  href={it.type === 'video' ? `https://tiktok.com/@${BRAND.tiktok}` : `https://instagram.com/${BRAND.instagram}`}
                  target="_blank"
                  rel="noreferrer"
                  className={`relative group rounded-2xl overflow-hidden aspect-square ${span} bg-[#F6EDFA]`}
                >
                  <img
                    src={it.src}
                    alt={it.caption}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <span className="text-[10px] text-white font-bold tracking-widest uppercase">{it.caption}</span>
                  </div>
                  {it.type === 'video' && (
                    <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 inline-flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#1F1A24]" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    </span>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* JELLYCIOUS Y2K universe band */}
      <section className="relative bg-[#A99BE0] py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-[#F4DD8C]/30 blur-2xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-[#FF7597]/30 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase text-white/80 mb-4">
            CONCEPTO · UNIVERSO JELLY
          </p>
          <h2
            className="font-brand text-[18vw] md:text-[14vw] lg:text-[12rem] leading-[0.85] text-[#F4DD8C]"
            style={{ textShadow: '4px 4px 0 rgba(31,26,36,0.08)' }}
          >
            JELLYCIOUS
          </h2>
          <p className="mt-6 max-w-3xl mx-auto text-white text-base md:text-xl tracking-wide">
            {lang === 'es'
              ? 'Un universo de belleza y bienestar que se siente '
              : 'A universe of beauty and wellbeing that feels '}
            <span className="font-display font-bold italic">
              {lang === 'es' ? 'rico, cercano y divertido' : 'rich, close and fun'}
            </span>
            .
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {(lang === 'es'
              ? ['#Dopaminebeauty', '#Foodcore', '#Y2K', '#JellyGirlies']
              : ['#Dopaminebeauty', '#Foodcore', '#Y2K', '#JellyGirlies']
            ).map((tag) => (
              <span key={tag} className="text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full bg-white/15 text-white border border-white/20 backdrop-blur">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip B2B */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-32 bg-[#FFFBFB]" data-testid={REG_IDS.expoBanner}>
        <div className="max-w-7xl mx-auto relative rounded-[2.5rem] bg-[#A99BE0] text-white p-10 md:p-16 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#F4DD8C]/40 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#FF7597]/30 blur-3xl" />
          <div className="absolute inset-0 grain pointer-events-none" />

          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#1F1A24] bg-[#F4DD8C] px-4 py-2 rounded-full">
                <Ticket className="w-3.5 h-3.5" /> {t('registration.raffleBadge')}
              </span>
              <h3 className="font-display mt-5 text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter text-balance leading-[0.95]">
                {lang === 'es'
                  ? 'Regístrate y participa por kits Jelly'
                  : 'Register & win Jelly kits'}
              </h3>
              <p className="mt-4 text-white/85 max-w-xl">
                {lang === 'es'
                  ? 'Cuéntanos un poco sobre ti y entra al sorteo de kits Jelly durante Expobelleza 2026.'
                  : 'Tell us a bit about you and enter the Jelly kits raffle at Expobelleza 2026.'}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/registro"
                  data-testid={REG_IDS.ctaBtn}
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#F4DD8C] text-[#1F1A24] text-sm font-bold tracking-widest uppercase hover:bg-white transition-colors shadow-[0_12px_40px_rgba(244,221,140,0.35)]"
                >
                  <Gift className="w-4 h-4" />
                  {t('registration.submit')}
                </Link>
                <Link
                  to="/distribuidores"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/15 text-white text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-[#1F1A24] transition-colors border border-white/30 backdrop-blur"
                >
                  {t('registration.submitDistributor')}
                </Link>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: 'x4', l: lang === 'es' ? 'Kits Jelly' : 'Jelly kits' },
                  { v: '4', l: lang === 'es' ? 'Sorteos' : 'Raffles' },
                  { v: '4', l: lang === 'es' ? 'Días feria' : 'Expo days' },
                  { v: '6', l: lang === 'es' ? 'Productos' : 'Products' },
                ].map((s, i) => (
                  <div key={i} className="rounded-3xl bg-white/10 border border-white/15 backdrop-blur p-5">
                    <p className="font-display font-black text-3xl text-[#F4DD8C] tracking-tighter">{s.v}</p>
                    <p className="text-[10px] mt-1 tracking-widest uppercase text-white/80">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA B2B dark */}
      <section className="px-6 md:px-12 lg:px-24 pb-20 md:pb-32 bg-[#FFFBFB]">
        <div className="max-w-7xl mx-auto relative rounded-[2.5rem] bg-[#1F1A24] text-white p-10 md:p-16 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#A99BE0]/30 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#F4DD8C]/30 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Heart className="w-6 h-6 text-[#F4DD8C] mb-4" />
              <h3 className="font-display text-3xl md:text-5xl font-black tracking-tighter text-balance">
                {lang === 'es' ? '¿Vendes belleza? Lleva Jelly a tu tienda.' : 'You sell beauty? Bring Jelly to your store.'}
              </h3>
            </div>
            <div className="md:justify-self-end">
              <Link
                to="/distribuidores"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#F4DD8C] text-[#1F1A24] text-sm font-bold tracking-widest uppercase hover:bg-white transition-colors"
              >
                {t('nav.distributors')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
